import * as React from 'react'
import { WithTranslation, withTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { AnyAction, Dispatch } from 'redux'
import styled, { css } from 'styled-components'
import ActionCableConsumer from '../../consumers/ActionCableConsumer'
import { AppState } from '../../store'
import { showConfirmModal } from '../../store/modals/actions'
import { ConversationMessageAuthorType, ConversationState, Conversation as ConversationType, CurrentUser, Sounds, WorkspaceChannelEvent, WorkspaceChannelEventType } from '../../types'
import PageLoader from '../Page/PageLoader'
import { matchPath, Route, RouteComponentProps, Switch, withRouter } from 'react-router-dom'
import { Style } from '../../styles'
import Icon from '../Icons/Icon'
import Header from './Header'
import HeaderTitle from './HeaderTitle'
import HeaderAction from './HeaderAction'
import ConversationItem from './ConversationItem'
import ResourceCheckbox from '../Resource/ResourceCheckbox'
import { ConversationsController } from '../../controllers'
import ERoute from '../../ERoute'
import Conversation from '../../containers/Conversations/Conversation'
import ConnectChannel from '../../containers/Conversations/ConnectChannel'
import RouteHelper from '../../helpers/RouteHelper'
import ConversationStatusPopover from './ConversationStatusPopover'
import ConversationSortPopover from './ConversationSortPopover'
import Utils from '../../utilities/Utils'
import Sound from '../../audio'
import { BREAKPOINT_SMALL_VALUE } from '../../styles/breakpoints'
import SimpleTooltip from '@/components/Tooltips/SimpleTooltip'

export enum ConversationSortColumn {
	// i18n.t('ConversationSortColumn::created_at')
	DATE_STARTED = 'created_at',
	// i18n.t('ConversationSortColumn::updated_at')
	LAST_ACTIVITY = 'updated_at',
	// i18n.t('ConversationSortColumn::priority')
	PRIORITY = 'priority',
}

const Container = styled.div`
	width: 100%;
	height: 100%;
	display: flex;
	flex-direction: row;
`

const LoaderContainer = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	flex: 1;
	width: 100%;
`

const ConversationsSidebar = styled.div<{ mobileActive: boolean }>`
	display: flex;
	flex-direction: column;
	flex-grow: 1;
	max-width: 270px;
	min-width: 270px;
	width: calc(32vw - 200px);
	transition: width .25s ease-in-out;
	border-right: 1px solid ${Style.color.border};
	background: white;
	overflow: hidden;
	z-index: 4;

	@media screen and (max-width: ${Style.breakpoints.SMALL}) {
		display: none;
		position: absolute;
		top: 0;
		right: 0;
		bottom: 0;
		left: 0;
		max-width: initial;
		width: 100%;
		z-index: 11;

		${props => props.mobileActive && css`
			display: flex;
		`}
	}
`

const ConversationsContainer = styled.div`
	display: flex;
	flex-direction: column;
	flex-grow: 1;
	flex: 1;
	padding: 12px 0;
	overflow: hidden;
`

const ConversationsHeader = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: space-between;
	padding: 0 12px;
	margin-bottom: 8px;
`

const ConversationsHeaderButtons = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 4px;
`

const ConversationsHeaderButton = styled.div`
	cursor: pointer;
	font-size: 13px;
	height: 24px;
	line-height: 1.2;
	color: #1a1a1a;
	border: 1px solid ${Style.color.border};
	font-weight: 600;
	padding: 4px 8px;
	background: white;
	width: fit-content;
	border-radius: 9999px;
	transition: background-color .1s ease-in-out;

	&:hover {
		background: #f0f1ef;
	}
`

const Conversations = styled.div`
	display: flex;
	flex-direction: column;
	flex-grow: 1;
	flex: 1;
	overflow-x: hidden;
	overflow-y: auto;
	padding: 0 12px;
	gap: 8px;
`

const ConversationsEmpty = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	flex: 1;
`

const ConversationsEmptyIcon = styled.div`
	svg {
		width: 40px;
		height: 40px;
	}

`

const ConversationsEmptyTitle = styled.div`
	font-size: 16px;
`

const MenuButton = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	height: 32px;
	width: 32px;
	padding: 8px;
	font-size: 14px;
	width: fit-content;
	cursor: pointer;
`

const HeaderTitleWrapper = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	width: 100%;
	overflow: hidden;
`

export enum InboxViewMode {
	INBOX = 'inbox',
	UNASSIGNED = 'unassigned',
	ALL = 'all',
}

interface IStateToProps {
	currentUser: CurrentUser
}
interface IDispatchToProps {
	showConfirmModal: typeof showConfirmModal
}

type IProps = {
	mode: InboxViewMode
	onToggleInboxMenu: () => void
} & IStateToProps & IDispatchToProps & WithTranslation & RouteComponentProps<any>

interface IState {
	conversations: ConversationType[]
	currentPage: number,
	endReached: boolean
	didInitialLoad: boolean
	isFetching: boolean
	filters: any,
	searchValue: string
	conversationStateFilter: ConversationState
	conversationSortColumn: ConversationSortColumn
	conversationSortDirection: 'asc' | 'desc'
	selectedConversationIds: string[]
}

class InboxView extends React.Component<IProps, IState> {
	private scrollContainer = React.createRef<HTMLDivElement>()

	constructor(props) {
		super(props)

		this.state = {
			conversations: [],
			currentPage: 0,
			didInitialLoad: false,
			endReached: false,
			isFetching: false,
			filters: {},
			searchValue: '',
			conversationStateFilter: ConversationState.OPEN,
			conversationSortColumn: ConversationSortColumn.DATE_STARTED,
			conversationSortDirection: 'desc',
			selectedConversationIds: []
		}

		this.fetchConversations = this.fetchConversations.bind(this)
		this.playNotification = Utils.debounce(this.playNotification.bind(this), 250)
		this.onConversationsEndReached = this.onConversationsEndReached.bind(this)
		this.onInboxFiltersChange = this.onInboxFiltersChange.bind(this)
		this.onInboxSearchChange = this.onInboxSearchChange.bind(this)
		this.onInboxSearchClear = this.onInboxSearchClear.bind(this)
		this.onInboxSearchKeyUp = this.onInboxSearchKeyUp.bind(this)
		this.onInboxClearFilters = this.onInboxClearFilters.bind(this)
		this.onConversationClick = this.onConversationClick.bind(this)
		this.onConversationSelectionChange = this.onConversationSelectionChange.bind(this)
		this.onConversationCloseToggle = this.onConversationCloseToggle.bind(this)
		this.onConversationTogglePriority = this.onConversationTogglePriority.bind(this)
		this.onToggleInboxMenu = this.onToggleInboxMenu.bind(this)
		this.onConversationStateFilterChange = this.onConversationStateFilterChange.bind(this)
		this.onConversationsSortColumnChange = this.onConversationsSortColumnChange.bind(this)
		this.onConversationsSortChange = this.onConversationsSortChange.bind(this)
		this.onToggleCloseConversationsClick = this.onToggleCloseConversationsClick.bind(this)
		this.onScrollChange = Utils.debounce(this.onScrollChange.bind(this), 250, false)
		this.onActionCableConnected = this.onActionCableConnected.bind(this)
		this.onActionCableDisconnected = this.onActionCableDisconnected.bind(this)
		this.onActionCableReceived = this.onActionCableReceived.bind(this)
	}

	componentDidUpdate(prevProps: Readonly<IProps>, prevState: Readonly<IState>, snapshot?: any): void {

		if (prevProps.mode !== this.props.mode) {
			this.fetchConversations(1).catch(console.error)
		}
	}

	componentDidMount() {
		this.fetchConversations(1).catch(console.error)
	}

	async fetchConversations(page: number) {
		try {
			const { mode, currentUser } = this.props
			const {
				conversations: stateConversations,
				searchValue,
				conversationSortColumn,
				conversationSortDirection,
				filters,
				selectedConversationIds,
				conversationStateFilter,
			} = this.state;

			let parameters = {
				page: page,
				search: searchValue,
				...filters,
				order: `${conversationSortColumn}_${conversationSortDirection}`
			}

			// Mode
			switch (mode) {
				case InboxViewMode.INBOX:
					parameters = { ...parameters, assignee_id: currentUser.id }
					break
				case InboxViewMode.ALL:
					parameters = { ...parameters }
					break
				case InboxViewMode.UNASSIGNED:
					parameters = { ...parameters, assignee_id: null }
					break
			}

			if (conversationStateFilter) parameters = { ...parameters, state: conversationStateFilter }

			const response = await ConversationsController.getConversations(parameters)

			const { conversations, current_page, total_pages, total_entries } = response;

			this.setState({
				conversations: page === 1 ? [...conversations] : [...stateConversations, ...conversations],
				currentPage: current_page,
				endReached: current_page === total_pages,
				didInitialLoad: true,
				isFetching: false,
				selectedConversationIds: page === 1 ? [] : selectedConversationIds
			}, () => {
				// Don't load the first conversation on mobile
				if (window.innerWidth >= BREAKPOINT_SMALL_VALUE) {
					const firstConversation = this.state.conversations[0]
					if (firstConversation) this.props.history.push(this.getConversationPath(firstConversation?.id))
				}

			})
		} catch (ex) {
			console.error(ex)
		}
	}

	playNotification(conversation: ConversationType) {
		const lastMessage = conversation.messages[conversation.messages.length - 1]
		if (!conversation.admin_read && lastMessage?.author?.type !== ConversationMessageAuthorType.USER) {
			Sound.play(Sounds.NOTIFICATION_ADMIN)
		}
	}

	onConversationsEndReached() {
		const { currentPage, endReached } = this.state
		if (!endReached) this.fetchConversations(currentPage + 1)
	}

	onInboxFiltersChange(filters: any) {
		this.setState({ filters: filters }, () => {
			this.fetchConversations(1)
		})
	}

	onInboxSearchChange(searchValue) {
		this.setState({ searchValue: searchValue })
	}

	onInboxSearchClear() {
		this.setState({ searchValue: '' }, () => this.fetchConversations(1))
	}

	onInboxSearchKeyUp(e) {
		if (e.key === 'Enter') {
			this.fetchConversations(1)
		}
	}

	onInboxClearFilters() {
		this.setState({
			searchValue: '',
			filters: {}
		}, () => this.fetchConversations(1))
	}

	onActionCableConnected() {
		console.log('[InboxView] connected')
	}

	onActionCableDisconnected() {
		console.log('[InboxView] Disconnected')
	}

	onActionCableReceived(event: WorkspaceChannelEvent) {
		console.log('[InboxView] received event', event)
		const { conversations } = this.state

		switch (event.type) {
			case WorkspaceChannelEventType.CONVERSATION_CREATE:
				const { conversation } = event.data
				const conversationIndex = conversations.findIndex(c => c.id === conversation.id)

				if (conversationIndex !== -1) return

				this.setState({ conversations: [conversation, ...conversations] })
				this.playNotification(conversation)
				break
			case WorkspaceChannelEventType.CONVERSATION_UPDATE:
				const { conversation: updatedConversation } = event.data
				const updateConversationIndex = conversations.findIndex(c => c.id === updatedConversation.id)

				if (updateConversationIndex === -1) {
					this.setState({ conversations: [updatedConversation, ...conversations] })
				} else {
					conversations[updateConversationIndex] = updatedConversation
					this.setState({ conversations: [...conversations] })
				}
				this.playNotification(updatedConversation)
				break
			case WorkspaceChannelEventType.CONVERSATION_DELETE:
				const { conversation_id } = event.data
				this.setState({ conversations: conversations.filter(c => c.id !== conversation_id) })
				break
			case WorkspaceChannelEventType.WIDGET_SESSION_UPDATE:
				const { widget_session } = event.data

				this.setState({
					conversations: conversations.map(conversation => {
						if (conversation.session_id === widget_session.id) {
							return {
								...conversation,
								user_data: {
									...conversation.user_data,
									last_seen_at: widget_session?.user_data?.last_seen_at
								}
							}
						} else {
							return conversation
						}
					})
				})
		}
	}

	getTitle() {
		const { mode, t, currentUser } = this.props

		switch (mode) {
			case InboxViewMode.INBOX:
				return currentUser.name || currentUser.email
			case InboxViewMode.UNASSIGNED:
				return t('Inbox::Unassigned')
			case InboxViewMode.ALL:
				return t('Inbox::All')
			default:
				throw new Error(`Unknown InboxViewMode: ${mode}`)
		}
	}

	getConversationPath(conversationId: string) {
		const { mode } = this.props
		let path: ERoute;
		switch (mode) {
			case InboxViewMode.INBOX:
				path = ERoute.PATH_INBOX_CONVERSATION
				break
			case InboxViewMode.ALL:
				path = ERoute.PATH_INBOX_ALL_CONVERSATION
				break
			case InboxViewMode.UNASSIGNED:
				path = ERoute.PATH_INBOX_UNASSIGNED_CONVERSATION
				break
			default:
				throw new Error(`Unknown InboxViewMode: ${mode}`)
		}

		return RouteHelper.process(path, { id: conversationId })
	}

	onConversationClick(conversation: ConversationType) {
		const { conversations } = this.state

		this.setState({
			conversations: conversations.map(existingConversation => {
				if (existingConversation.id === conversation.id) return { ...existingConversation, admin_read: true }

				return existingConversation
			})
		})


		this.props.history.push(this.getConversationPath(conversation.id))
	}

	onConversationSelectionChange(conversation: ConversationType) {
		const { selectedConversationIds } = this.state

		this.setState({
			selectedConversationIds: selectedConversationIds.includes(conversation.id) ?
				selectedConversationIds.filter(id => id !== conversation.id) :
				[...selectedConversationIds, conversation.id]
		})
	}

	async onConversationCloseToggle(conversation: ConversationType) {
		const { conversations } = this.state
		try {
			const updatedConversation = { ...conversation, state: conversation.state === ConversationState.OPEN ? ConversationState.CLOSED : ConversationState.OPEN }

			await ConversationsController.update({ ...updatedConversation })

			const conversationIndex = conversations.findIndex((c) => c.id === conversation.id);

			if (conversationIndex === -1) return

			conversations[conversationIndex] = updatedConversation

			this.setState({ conversations: [...conversations] })
		} catch (ex) {
			console.error(ex)
		}
	}

	async onConversationTogglePriority(conversation: ConversationType) {
		const { conversations } = this.state

		try {
			const updatedConversation = await ConversationsController.update({ ...conversation, priority: !conversation.priority });

			const conversationIndex = conversations.findIndex((c) => c.id === conversation.id);

			if (conversationIndex === -1) return

			conversations[conversationIndex] = updatedConversation

			this.setState({ conversations: [...conversations] })
		} catch (ex) {
			console.error(ex)
		}
	}


	onToggleInboxMenu() {
		this.props.onToggleInboxMenu()
	}

	onConversationStateFilterChange(status: ConversationState) {
		this.setState({ conversationStateFilter: status }, () => {
			this.fetchConversations(1)
		})
	}

	onConversationsSortColumnChange(column: ConversationSortColumn) {
		this.setState({ conversationSortColumn: column }, () => {
			this.fetchConversations(1)
		})
	}

	onConversationsSortChange() {
		const { conversationSortDirection } = this.state

		this.setState({ conversationSortDirection: conversationSortDirection === 'asc' ? 'desc' : 'asc' }, () => {
			this.fetchConversations(1)
		})
	}

	async onToggleCloseConversationsClick() {
		try {
			const { selectedConversationIds, conversations, conversationStateFilter } = this.state

			if (conversationStateFilter === ConversationState.CLOSED) {
				this.setState({
					selectedConversationIds: [],
					conversations: conversations.map(conversation => {
						if (selectedConversationIds.includes(conversation.id)) {
							return ({ ...conversation, state: ConversationState.OPEN })
						} else {
							return conversation
						}
					})
				})

				await ConversationsController.bulkOpen(selectedConversationIds);
			}
			else {
				this.setState({
					selectedConversationIds: [],
					conversations: conversations.map(conversation => {
						if (selectedConversationIds.includes(conversation.id)) {
							return ({ ...conversation, state: ConversationState.CLOSED })
						} else {
							return conversation
						}
					})
				})

				await ConversationsController.bulkClose(selectedConversationIds);
			}
		} catch (ex) {
			console.error(ex)
		}
	}

	onScrollChange(event) {
		const { endReached, currentPage } = this.state
		const node = this.scrollContainer.current;

		if (!node) return

		const scrollContainereEndReached = node.scrollHeight - node.scrollTop - 200 <= node.clientHeight;

		if (!endReached && scrollContainereEndReached) {
			this.fetchConversations(currentPage + 1).catch(console.error)
		}
	}

	getFilteredConversations() {
		const { mode, currentUser } = this.props
		const { conversations, conversationStateFilter } = this.state

		return conversations
			.filter(c => c.state === conversationStateFilter)
			.filter(c => {
				switch (mode) {
					case InboxViewMode.INBOX:
						return c.assignee_id === currentUser.id
					case InboxViewMode.UNASSIGNED:
						return !Boolean(c.assignee_id)
					case InboxViewMode.ALL:
					default:
						return true
				}
			})
	}

	render() {
		const { currentUser, t, mode } = this.props
		const { workspace: { setting } } = currentUser
		const {
			didInitialLoad,
			conversationStateFilter,
			conversationSortColumn,
			conversationSortDirection,
			selectedConversationIds
		} = this.state

		const activePath = location.pathname
		const conversations = this.getFilteredConversations()
		const itemsSelected = selectedConversationIds?.length > 0
		const fullySelected = selectedConversationIds?.length === conversations?.length
		const partiallySelected = selectedConversationIds?.length !== 0 && selectedConversationIds?.length < conversations?.length
		const noneSelected = selectedConversationIds?.length === 0

		return (
			<Container>
				{!didInitialLoad && <LoaderContainer><PageLoader /></LoaderContainer>}
				{didInitialLoad &&
					<ActionCableConsumer
						channel={{ channel: 'WorkspaceChannel', id: currentUser.workspace.id }}
						onConnected={this.onActionCableConnected}
						onDisconnected={this.onActionCableDisconnected}
						onReceived={this.onActionCableReceived}
					>
						<ConversationsSidebar
							mobileActive={
								!!matchPath(activePath, { path: ERoute.PATH_INBOX_ALL, exact: true }) ||
								!!matchPath(activePath, { path: ERoute.PATH_INBOX_ME, exact: true }) ||
								!!matchPath(activePath, { path: ERoute.PATH_INBOX_UNASSIGNED, exact: true })
							}
						>
							<Header>
								<HeaderTitleWrapper>
									<SimpleTooltip content={t('InboxView::Toggle menu')}>
										<MenuButton onClick={this.onToggleInboxMenu}>
											<Icon icon='menu-left' />
										</MenuButton>
									</SimpleTooltip>

									<HeaderTitle>
										{this.getTitle()}
									</HeaderTitle>
								</HeaderTitleWrapper>
							</Header>

							<ConversationsContainer>
								<ConversationsHeader>
									{!itemsSelected && <>
										<ConversationsHeaderButtons>
											<ConversationStatusPopover
												activator={
													<ConversationsHeaderButton>
														{conversations?.length} {t(`ConversationState::${conversationStateFilter}`).toLowerCase()}
													</ConversationsHeaderButton>
												}
												status={conversationStateFilter}
												onSubmit={this.onConversationStateFilterChange}
											/>

										</ConversationsHeaderButtons>

										<ConversationsHeaderButtons>
											<ConversationSortPopover
												activator={
													<ConversationsHeaderButton>
														{t(`ConversationSortColumn::${conversationSortColumn}`)}
													</ConversationsHeaderButton>
												}
												value={conversationSortColumn}
												onSubmit={this.onConversationsSortColumnChange}
											/>

											<ConversationsHeaderButton onClick={this.onConversationsSortChange}>
												<Icon icon={conversationSortDirection === 'desc' ? 'sort-descending' : 'sort-ascending'} />
											</ConversationsHeaderButton>
										</ConversationsHeaderButtons>
									</>}
									{itemsSelected && <>
										<ConversationsHeaderButtons>
											<ResourceCheckbox
												checked={fullySelected || partiallySelected}
												onCheckedChange={() => {

													if (noneSelected || partiallySelected) {
														this.setState({ selectedConversationIds: conversations.map(c => c.id) })
													} else {
														this.setState({ selectedConversationIds: [] })
													}
												}}
												partial={partiallySelected}
											/>

											<span style={{ marginLeft: 6 }}>
												{t('InboxView::{{count}} selected', { count: selectedConversationIds.length })}
											</span>
										</ConversationsHeaderButtons>

										<ConversationsHeaderButtons onClick={this.onToggleCloseConversationsClick}>
											<SimpleTooltip content={conversationStateFilter === ConversationState.CLOSED ? t('InboxView::Re-open selection') : t('InboxView::Close selection')}>
												<HeaderAction type='primary'>
													<Icon icon={conversationStateFilter === ConversationState.CLOSED ? 'archive-arrow-up' : 'archive-arrow-down'} />
												</HeaderAction>
											</SimpleTooltip>
										</ConversationsHeaderButtons>
									</>}
								</ConversationsHeader>

								<Conversations ref={this.scrollContainer} onScroll={this.onScrollChange}>
									{conversations?.length > 0 && <>
										{conversations.map((conversation, index) => {
											return (
												<ConversationItem
													key={conversation.id}
													conversation={conversation}
													active={!!matchPath(this.props.location.pathname, this.getConversationPath(conversation.id))}
													onConversationClick={this.onConversationClick}
													selected={selectedConversationIds.includes(conversation.id)}
													onSelectionChange={this.onConversationSelectionChange}
													onConversationCloseToggle={this.onConversationCloseToggle}
													onConversationTogglePriority={this.onConversationTogglePriority}
												/>
											)
										})}
									</>}

									{conversations?.length === 0 && <>
										<ConversationsEmpty>
											<ConversationsEmptyIcon>
												<Icon icon='messenger-conversations-active' />
											</ConversationsEmptyIcon>

											<ConversationsEmptyTitle>
												{t('InboxView::No conversations')}
											</ConversationsEmptyTitle>
										</ConversationsEmpty>
									</>}
								</Conversations>
							</ConversationsContainer>
						</ConversationsSidebar>

						<Switch>
							<Route path={ERoute.PATH_INBOX_CONVERSATION} component={Conversation} />
							<Route path={ERoute.PATH_INBOX_ALL_CONVERSATION} component={Conversation} />
							<Route path={ERoute.PATH_INBOX_UNASSIGNED_CONVERSATION} component={Conversation} />
							<Route path={ERoute.PATH_INBOX} component={ConnectChannel} />
							<Route path={ERoute.PATH_INBOX_ALL} component={ConnectChannel} />
							<Route path={ERoute.PATH_INBOX_UNASSIGNED} component={ConnectChannel} />
						</Switch>
					</ActionCableConsumer>}
			</Container>
		)
	}
}


const mapStateToProps = (state: AppState): IStateToProps => {
	const {
		authentication: {
			currentUser,
		}
	} = state

	return {
		currentUser: currentUser,
	}
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): IDispatchToProps => {
	return {
		showConfirmModal: (options) => dispatch(showConfirmModal(options))
	}
}


export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(withRouter(InboxView)))